import { useState, useEffect, useCallback } from 'react';
import { useBrandingContext } from '@/contexts';
import { ThemeConfig } from '@goalsetter/ant-design-ui';
import lightTheme from '@/assets/lightTheme.json';
import { resolveBrandingStylesFetchUrl } from '@/utils/brandingStylesUrl';

export interface BrandingStylesData {
  styles: ThemeConfig | null;
  isLoading: boolean;
  error: string | null;
  stylesVersion: string | null;
  clearStylesCache: (versionUrl?: string) => void;
  refreshStyles: () => void;
}

// Cache for branding styles to avoid repeated fetches
const stylesCache = new Map<string, ThemeConfig>();

/**
 * Hook to load and manage branding styles
 * @returns BrandingStylesData with styles and loading state
 */
export const useBrandingStyles = (): BrandingStylesData => {
  const { getStylesVersion, branding } = useBrandingContext();
  const [styles, setStyles] = useState<ThemeConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stylesVersion, setStylesVersion] = useState<string | null>(null);

  const loadStyles = useCallback(async (versionUrl: string) => {
    // Check cache first
    if (stylesCache.has(versionUrl)) {
      setStyles(stylesCache.get(versionUrl) as ThemeConfig);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(resolveBrandingStylesFetchUrl(versionUrl), {
        credentials: 'omit',
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(
          `Failed to load styles: ${response.status} ${response.statusText}`,
        );
      }

      const themeConfig: ThemeConfig = await response.json();

      // Cache the styles
      stylesCache.set(versionUrl, themeConfig);

      setStyles(themeConfig);
      setError(null);
    } catch (err) {
      setStyles(lightTheme as unknown as ThemeConfig);
      console.error('Error loading branding styles:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load branding styles',
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load styles when branding changes
  useEffect(() => {
    const currentStylesVersion = getStylesVersion();
    setStylesVersion(currentStylesVersion);

    if (currentStylesVersion) {
      loadStyles(currentStylesVersion);
    } else {
      // Clear styles if no stylesVersion
      setStyles(null);
      setError(null);
    }
  }, [branding, getStylesVersion, loadStyles]);

  // Clear cache for a specific version
  const clearStylesCache = useCallback((versionUrl?: string) => {
    if (versionUrl) {
      stylesCache.delete(versionUrl);
    } else {
      stylesCache.clear();
    }
  }, []);

  // Refresh styles
  const refreshStyles = useCallback(() => {
    const currentStylesVersion = getStylesVersion();
    if (currentStylesVersion) {
      // Clear cache and reload
      stylesCache.delete(currentStylesVersion);
      loadStyles(currentStylesVersion);
    }
  }, [getStylesVersion, loadStyles]);

  return {
    styles,
    isLoading,
    error,
    stylesVersion,
    clearStylesCache,
    refreshStyles,
  };
};
